import { Button } from '@/components/atoms';
import { Typography } from '@/components/atoms/Typography';
import {
  BankTransferInfoIcon,
  CopyWebIcon,
  DirhamIcon,
} from '@/components/atoms/icons';
import { ContentDialog } from '@/components/molecules/dialogs/ContentDialog';
import { Toast } from '@/components/molecules/Toast';
import { logger } from '@/core/logger';
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from '@/core/constants/dynatraceCatalog';
import { useTranslation, useUIColors } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { hasNumericValue, hasTextValue } from '@/core/utils/fieldAvailability';
import * as Clipboard from 'expo-clipboard';
import React, { useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import type { BankTransferAccount } from '../constants/bankTransferDetails';
import type { UnitDetailsLabels } from '../constants/getPropertyLabels';
import { useBankTransferAccount } from '../hooks/useBankTransferAccount';
import { usePropertyLabels } from '../hooks/usePropertyLabels';
import {
  PaymentFlowMobilePairFooter,
  PaymentFlowMobileScreen,
  useIsPaymentPhoneLayout,
} from './PaymentFlowMobileScreen';

const DIALOG_WIDTH = 593;
const DIALOG_HEIGHT = 600;
/** Figma title / label / note fills (light) */
const TITLE_COLOR = '#333333';
const FIELD_LABEL_COLOR = '#666666';
const NOTE_TEXT_COLOR = '#4D4D4D';
const NOTE_ICON_COLOR = '#922A22';

export interface BankTransferDetailsDialogProps {
  visible: boolean;
  onBack: () => void;
  onConfirmTransfer: () => void;
  onClose: () => void;
  /** When set and > 0, show the Amount Paid row (Figma optional variant). */
  amountPaid?: number | undefined;
  confirmDisabled?: boolean;
  /**
   * Installment escrow account. When omitted, the dialog reads the
   * profile-details account used by the ADHA Pay Now flow.
   */
  account?: BankTransferAccount | null;
  /**
   * View-only: show the account and a Close action.
   * Skips the ADHA "I've made the transfer" step.
   */
  viewOnly?: boolean;
}

type TransferColors = ReturnType<typeof useUIColors>;

async function copyValue(value: string): Promise<boolean> {
  try {
    await Clipboard.setStringAsync(value);
    return true;
  } catch (error) {
    logger.error('BankTransferDetailsDialog: copy failed', error);
    return false;
  }
}

function TransferField({
  label,
  value,
}: Readonly<{
  label: string;
  value: string;
}>) {
  return (
    <View style={styles.field}>
      <Typography
        variant="caption"
        color={FIELD_LABEL_COLOR}
        style={styles.fieldLabel}
      >
        {label}
      </Typography>
      <Typography variant="headline" color="primary" style={styles.fieldValue}>
        {value}
      </Typography>
    </View>
  );
}

function CopyableField({
  label,
  value,
  copyA11yLabel,
  colors,
  onCopied,
}: Readonly<{
  label: string;
  value: string;
  copyA11yLabel: string;
  colors: TransferColors;
  onCopied: () => void;
}>) {
  const handleCopy = async () => {
    const ok = await copyValue(value);
    if (ok) {
      onCopied();
    }
  };

  return (
    <View style={styles.copyableRow}>
      <View style={styles.copyableCopy}>
        <Typography
          variant="caption"
          color={FIELD_LABEL_COLOR}
          style={styles.fieldLabel}
        >
          {label}
        </Typography>
        <Typography variant="headline" color="primary" style={styles.fieldValue}>
          {value}
        </Typography>
      </View>
      <Pressable
        onPress={() => {
          void handleCopy();
        }}
        accessibilityRole="button"
        accessibilityLabel={copyA11yLabel}
        style={[
          styles.copyButton,
          { backgroundColor: colors.backgroundLevel2 },
        ]}
      >
        <CopyWebIcon size={16} color={colors.fontPrimary} />
      </Pressable>
    </View>
  );
}

function AmountPaidField({
  label,
  amount,
  colors,
}: Readonly<{
  label: string;
  amount: number;
  colors: TransferColors;
}>) {
  return (
    <View style={styles.field}>
      <Typography
        variant="caption"
        color={FIELD_LABEL_COLOR}
        style={styles.fieldLabel}
      >
        {label}
      </Typography>
      <View style={styles.amountPaidRow}>
        <DirhamIcon size={16} color={colors.fontPrimary} />
        <Typography variant="headline" color="primary" style={styles.fieldValue}>
          {amount.toLocaleString()}
        </Typography>
      </View>
    </View>
  );
}

type TransferSection = {
  key: string;
  node: React.ReactNode;
};

function pushTextSection(
  sections: TransferSection[],
  key: string,
  label: string,
  value: string,
): void {
  if (!hasTextValue(value)) {
    return;
  }
  sections.push({
    key,
    node: <TransferField label={label} value={value} />,
  });
}

function pushCopySection(
  sections: TransferSection[],
  key: string,
  label: string,
  value: string,
  colors: TransferColors,
  onCopied: () => void,
): void {
  if (!hasTextValue(value)) {
    return;
  }
  sections.push({
    key,
    node: (
      <CopyableField
        label={label}
        value={value}
        copyA11yLabel={label}
        colors={colors}
        onCopied={onCopied}
      />
    ),
  });
}

function TransferAccountCard({
  labels,
  colors,
  account,
  amountPaid,
  onCopied,
  hugHeight,
}: Readonly<{
  labels: UnitDetailsLabels;
  colors: TransferColors;
  account: BankTransferAccount | null;
  amountPaid: number | undefined;
  onCopied: () => void;
  hugHeight?: boolean;
}>) {
  const sections: TransferSection[] = [];

  pushTextSection(
    sections,
    'accountName',
    labels.ACCOUNT_NAME,
    account?.accountName ?? '',
  );
  if (hasNumericValue(amountPaid)) {
    sections.push({
      key: 'amountPaid',
      node: (
        <AmountPaidField
          label={labels.AMOUNT_PAID}
          amount={amountPaid}
          colors={colors}
        />
      ),
    });
  }
  pushTextSection(
    sections,
    'bankName',
    labels.BANK_NAME,
    account?.bankName ?? '',
  );
  pushCopySection(
    sections,
    'accountNumber',
    labels.ACCOUNT_NUMBER,
    account?.accountNumber ?? '',
    colors,
    onCopied,
  );
  pushCopySection(
    sections,
    'iban',
    labels.IBAN,
    account?.iban ?? '',
    colors,
    onCopied,
  );
  pushCopySection(
    sections,
    'swiftCode',
    labels.SWIFT_CODE,
    account?.swiftCode ?? '',
    colors,
    onCopied,
  );

  return (
    <View
      style={[
        styles.accountCard,
        hugHeight ? styles.accountCardHug : null,
        {
          backgroundColor: colors.backgroundLevel1,
          borderColor: colors.outlineDisabled,
        },
      ]}
    >
      <Typography
        variant="headlineLarge"
        color="primary"
        style={styles.sectionTitle}
      >
        {labels.TRANSFER_TO_THIS_ACCOUNT}
      </Typography>
      {sections.map((section, index) => (
        <React.Fragment key={section.key}>
          {index > 0 ? (
            <View
              style={[
                styles.accountDivider,
                { backgroundColor: colors.outlineDisabled },
              ]}
            />
          ) : null}
          <View style={styles.accountSection}>{section.node}</View>
        </React.Fragment>
      ))}
    </View>
  );
}

function TransferProofNote({
  labels,
  noteEmail,
  compact,
}: Readonly<{
  labels: UnitDetailsLabels;
  noteEmail: string;
  compact: boolean;
}>) {
  return (
    <View style={[styles.noteRow, compact ? styles.noteRowMobile : null]}>
      <BankTransferInfoIcon size={20} color={NOTE_ICON_COLOR} />
      <Typography
        variant="footnote"
        color={NOTE_TEXT_COLOR}
        style={[styles.noteCopy, compact ? styles.noteCopyMobile : null]}
      >
        {labels.BANK_TRANSFER_NOTE_PREFIX}
        {noteEmail.length > 0 ? ` ${noteEmail}` : null}
      </Typography>
    </View>
  );
}

/**
 * ADHA Pay Now omits `accountOverride` and uses the profile account.
 * View Bank Details always uses the installment account, including when it is null.
 */
function resolveTransferAccount(
  viewOnly: boolean,
  accountOverride: BankTransferAccount | null | undefined,
  storeAccount: BankTransferAccount | null,
): BankTransferAccount | null {
  if (viewOnly) {
    return accountOverride ?? null;
  }
  if (accountOverride === undefined) {
    return storeAccount;
  }
  return accountOverride;
}

/**
 * Bank Transfer Details — account numbers with copy, optional amount paid.
 */
export function BankTransferDetailsDialog({
  visible,
  onBack,
  onConfirmTransfer,
  onClose,
  amountPaid,
  confirmDisabled = false,
  account: accountOverride,
  viewOnly = false,
}: Readonly<BankTransferDetailsDialogProps>) {
  const colors = useUIColors();
  const { t } = useTranslation();
  const isPhone = useIsPaymentPhoneLayout();
  const { UNIT_DETAILS: labels } = usePropertyLabels();
  const storeAccount = useBankTransferAccount();
  const account = resolveTransferAccount(viewOnly, accountOverride, storeAccount);
  const noteEmail = labels.SUBMIT_PROOF_EMAIL.trim();
  const [copiedVisible, setCopiedVisible] = useState(false);

  React.useEffect(() => {
    if (!visible || viewOnly) {
      if (!visible) {
        setCopiedVisible(false);
      }
      return;
    }
    trackCatalogEvent({
      screen: ALMETLAA_SCREEN.TOP_UP,
      actionName: ALMETLAA_ACTION.TOP_UP.BANK_TRANSFER_DIALOG_VIEW,
      eventType: 'view',
    });
  }, [visible, viewOnly]);

  const handleMadeTransfer = () => {
    trackCatalogEvent({
      screen: ALMETLAA_SCREEN.TOP_UP,
      actionName: ALMETLAA_ACTION.TOP_UP.BANK_TRANSFER_MADE_CLICK,
      eventType: 'action',
    });
    onConfirmTransfer();
  };

  const body = (
    <View style={styles.body}>
      <TransferAccountCard
        labels={labels}
        colors={colors}
        account={account}
        amountPaid={amountPaid}
        onCopied={() => setCopiedVisible(true)}
        hugHeight={isPhone}
      />
      <TransferProofNote
        labels={labels}
        noteEmail={noteEmail}
        compact={isPhone}
      />
    </View>
  );

  const copiedToast = (
    <Toast
      visible={copiedVisible}
      message={labels.COPIED_TO_CLIPBOARD}
      position="top-right"
      onHide={() => setCopiedVisible(false)}
    />
  );

  const closeLabel = t('common.close');
  const phoneFooter = viewOnly ? (
    <View style={styles.closeFooter}>
      <Button
        title={closeLabel}
        variant="primary"
        onPress={onClose}
        style={styles.closeButton}
        textStyle={styles.confirmLabel}
      />
    </View>
  ) : (
    <PaymentFlowMobilePairFooter
      secondaryLabel={labels.CANCEL_RESERVATION_BUTTON}
      primaryLabel={labels.IVE_MADE_THE_TRANSFER}
      onSecondary={onClose}
      onPrimary={handleMadeTransfer}
      primaryDisabled={confirmDisabled}
    />
  );
  const desktopActions = viewOnly
    ? [
        {
          label: closeLabel,
          variant: 'primary' as const,
          flex: false,
          fullWidth: false,
          onPress: onClose,
          style: styles.closeButton,
          textStyle: styles.confirmLabel,
        },
      ]
    : [
        {
          label: labels.CANCEL_RESERVATION_BUTTON,
          variant: 'text' as const,
          flex: false,
          fullWidth: false,
          onPress: onClose,
          style: styles.backButton,
          textStyle: [styles.backLabel, styles.cancelLink],
        },
        {
          label: labels.IVE_MADE_THE_TRANSFER,
          variant: 'primary' as const,
          flex: false,
          fullWidth: false,
          disabled: confirmDisabled,
          onPress: handleMadeTransfer,
          style: styles.confirmButton,
          textStyle: styles.confirmLabel,
        },
      ];

  if (isPhone) {
    return (
      <>
        <PaymentFlowMobileScreen
          visible={visible}
          title={labels.BANK_TRANSFER_DETAILS_TITLE}
          onBack={onBack}
          backAccessibilityLabel={viewOnly ? closeLabel : labels.CANCEL_RESERVATION_BUTTON}
          footer={phoneFooter}
        >
          {body}
        </PaymentFlowMobileScreen>
        {copiedToast}
      </>
    );
  }

  return (
    <>
      <ContentDialog
        visible={visible}
        onClose={onClose}
        contentAlign="left"
        maxWidth={DIALOG_WIDTH}
        title={labels.BANK_TRANSFER_DETAILS_TITLE}
        titleVariant="title1"
        titleColor={TITLE_COLOR}
        titleStyle={styles.title}
        headerStyle={styles.header}
        cardStyle={[
          styles.card,
          styles.cardDesktop,
          {
            backgroundColor: colors.backgroundLevel2,
            ...(Platform.OS === 'web'
              ? ({ height: 'auto', maxHeight: 'none' } as object)
              : null),
          },
        ]}
        actionsStyle={viewOnly ? styles.closeActions : styles.actions}
        closeOnBackdropPress
        actions={desktopActions}
      >
        {body}
      </ContentDialog>
      {copiedToast}
    </>
  );
}

const styles = StyleSheet.create({
  /** Figma: 593×600, gap 32, radius 16, padding 32 */
  card: {
    width: '100%',
    borderRadius: 16,
    paddingTop: 32,
    paddingRight: 32,
    paddingBottom: 32,
    paddingLeft: 32,
    gap: 32,
    ...(Platform.OS === 'web'
      ? ({ boxSizing: 'border-box' } as object)
      : null),
  },
  cardDesktop: {
    width: DIALOG_WIDTH,
    minWidth: DIALOG_WIDTH,
    maxWidth: DIALOG_WIDTH,
    minHeight: DIALOG_HEIGHT,
    flexShrink: 0,
    justifyContent: 'flex-start',
  },
  header: {
    gap: 0,
  },
  /** Figma Matter Medium 32 / 100%, #333333 */
  title: {
    fontFamily: 'Matter-Medium',
    fontWeight: Platform.OS === 'web' ? '400' : '500',
    fontSize: 32,
    lineHeight: 32,
    ...(Platform.OS === 'web'
      ? ({
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        } as object)
      : null),
  },
  body: {
    gap: 24,
    flexGrow: 0,
    flexShrink: 0,
    minHeight: 0,
  },
  /** Figma: 529×378 — title then fields; bars centered between rows */
  accountCard: {
    width: '100%',
    height: 378,
    borderRadius: 12,
    borderWidth: 1,
    paddingTop: 14,
    paddingRight: 24,
    paddingBottom: 14,
    paddingLeft: 24,
    justifyContent: 'space-between',
    ...(Platform.OS === 'web'
      ? ({ boxSizing: 'border-box' } as object)
      : null),
  },
  accountCardHug: {
    height: undefined,
    minHeight: 0,
    gap: 12,
  },
  /** Figma Matter SemiBold 18 / 100% */
  sectionTitle: {
    fontFamily: 'Matter-SemiBold',
    fontWeight: Platform.OS === 'web' ? '400' : '600',
    fontSize: 18,
    lineHeight: 18,
    ...(Platform.OS === 'web'
      ? ({
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        } as object)
      : null),
  },
  accountSection: {
    flexShrink: 0,
  },
  /** Hairline bar centered in the gap between field rows */
  accountDivider: {
    height: 1,
    width: '100%',
    alignSelf: 'center',
    flexShrink: 0,
  },
  divider: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    width: '100%',
  },
  field: {
    gap: 4,
  },
  /** Figma Matter Medium 12 / 100%, uppercase, #666666 */
  fieldLabel: {
    fontFamily: 'Matter-Medium',
    fontWeight: Platform.OS === 'web' ? '400' : '500',
    fontSize: 12,
    lineHeight: 12,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    ...(Platform.OS === 'web'
      ? ({
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        } as object)
      : null),
  },
  fieldValue: {
    fontFamily: 'Matter-SemiBold',
    fontWeight: Platform.OS === 'web' ? '400' : '600',
    fontSize: 16,
    lineHeight: 16,
  },
  copyableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  copyableCopy: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  copyButton: {
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  amountPaidRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  noteRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    flexShrink: 0,
    overflow: 'visible',
    marginTop: 0,
    marginLeft: 0,
    marginRight: 0,
    paddingLeft: 0,
  },
  noteRowMobile: {
    alignItems: 'center',
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
    paddingLeft: 0,
  },
  /** One paragraph so wrap uses line-height, not flex rowGap */
  noteCopy: {
    flex: 1,
    minWidth: 0,
    fontFamily: 'Matter-Regular',
    fontWeight: '400',
    fontSize: 13,
    lineHeight: 20,
    letterSpacing: 0,
  },
  /** Phone: tighter wrap between “payment proof to” and the email */
  noteCopyMobile: {
    lineHeight: 16,
  },
  actions: {
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  closeActions: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeFooter: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButton: {
    alignSelf: 'center',
    width: 189,
    height: 48,
    minWidth: 189,
    borderRadius: 4,
  },
  backLabel: {
    fontFamily: 'Matter-SemiBold',
    fontWeight: Platform.OS === 'web' ? '400' : '600',
    fontSize: 16,
    lineHeight: 22,
    textAlign: 'center',
  },
  cancelLink: {
    textDecorationLine: 'underline',
    textDecorationStyle: 'solid',
    paddingBottom: 2,
    ...(Platform.OS === 'web'
      ? ({ textUnderlineOffset: 3 } as object)
      : null),
  },
  backButton: {
    height: 40,
    paddingHorizontal: 0,
    overflow: 'visible',
  },
  confirmButton: {
    width: 189,
    height: 48,
    minWidth: 189,
    gap: 4,
    borderRadius: 4,
    paddingTop: 2,
    paddingRight: 2,
    paddingBottom: 2,
    paddingLeft: 2,
  },
  confirmLabel: {
    fontFamily: 'Matter-SemiBold',
    fontWeight: Platform.OS === 'web' ? '400' : '600',
    fontSize: 14,
    lineHeight: 14,
    textAlign: 'center',
  },
});

export default BankTransferDetailsDialog;
